import React from 'react';
import { useQueryParams } from 'apps/website/src/components/BlockComponents/TablePriceBlockComponent/scripts/hooks/useQueryParam';
import { BlocksType } from 'apps/website/src/components/TemplateBlocksZone/BlocksType';
import {
  ConditionalBlockRecord,
  NbConditionalBlockRecord,
  NbConditionRecord,
} from 'apps/website/types/dato';

export interface ConditionalBlockRecordProps {
  block: ConditionalBlockRecord;
}

// Evaluate if a condition matches the query parameters
const evaluateCondition = (
  condition: NbConditionRecord,
  queryParams: Record<string, any>,
): boolean => {
  if (!condition.key) {
    return false;
  }

  if (!(condition.key in queryParams) && condition.operator !== 'NEQ') {
    return false;
  }

  const paramValue = queryParams[condition.key];

  switch (condition.operator) {
    case 'EQ':
      // Equal to
      return paramValue === condition.value;
    case 'NEQ':
      // Not Equal to
      return paramValue !== condition.value;
    case 'LTE':
      // Less than or equal to
      return Number(paramValue) <= Number(condition.value);
    case 'GTE':
      // Greater than or equal to
      return Number(paramValue) >= Number(condition.value);
    default:
      return false;
  }
};

// Finds the appropriate block based on conditions
export const useFindBlock = (block: any) => {
  const [queryParams] = useQueryParams();

  if (block.__typename !== 'DatoCmsConditionalBlock') {
    return block;
  }

  const items: NbConditionalBlockRecord[] = block.items;

  let defaultBlock: any = null;

  // First, find a block with matching conditions
  const blockWithMatchingConditions = items.find((item) => {
    // If no conditions, skip this item
    if (!item.conditions || item.conditions.length === 0) {
      defaultBlock = defaultBlock || item.block;
      return false;
    }

    // Check if all conditions match
    return item.conditions.every((condition) =>
      evaluateCondition(condition, queryParams),
    );
  });

  // If we found a block with matching conditions, return it
  if (blockWithMatchingConditions) {
    return blockWithMatchingConditions.block;
  }

  // Otherwise, return the first block without conditions (fallback)
  const fallbackItem = items.find(
    (item) => !item.conditions || item.conditions.length === 0,
  );

  return fallbackItem?.block || null;
};

const ConditionalBlockComponent = ({
  block,
  ...props
}: ConditionalBlockRecordProps) => {
  const blockToDisplay = useFindBlock(block.items);

  if (!blockToDisplay) {
    return null;
  }

  return <BlocksType {...props} block={blockToDisplay} />;
};

export default ConditionalBlockComponent;
